import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useColorModeValue,
  useDisclosure,
  useMultiStyleConfig,
  useToken,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { AddReactionIcon } from 'components/icons';
import { useCustomTheme } from 'apps/TenantApp/theme/CustomThemeProvider/useCustomTheme';
import data from '@emoji-mart/data';
import {
  init as martInit,
  Picker as MartPicker,
  Store as MartStore,
} from 'emoji-mart';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFeatureFlag } from 'utils/sessionProvider';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import useClickOutside from 'hooks/useClickOutside';

martInit({ data });

interface EmojiPickerProps {
  onEmojiPick: (emoji: string) => void;
}

type EmojiSkin = 1 | 2 | 3 | 4 | 5 | 6;

export const EmojiPicker = ({ onEmojiPick }: EmojiPickerProps) => {
  const ModalStyleConfig = useMultiStyleConfig('Modal');
  const { colorMode } = useCustomTheme();
  const layout = useActiveLayout();

  const { isOpen, onOpen, onClose: _onClose } = useDisclosure();
  const {
    isOpen: _isExpanded,
    onOpen: onExpand,
    onClose: onCloseExpand,
  } = useDisclosure();

  // We want to control the rollout of quick select
  // So when the feature flag disables it, we force the `isExpanded` variable to be true
  const allowEmojiQuickSelect = useFeatureFlag('FEATURE_EMOJI_QUICK_SELECT');
  const skipEmojiQuickSelect = !allowEmojiQuickSelect;
  const isExpanded = _isExpanded || skipEmojiQuickSelect;

  const onClose = () => {
    onCloseExpand();
    _onClose();
  };

  const floatingPickerRef = useRef(null);
  useClickOutside(floatingPickerRef, () => {
    if (floatingPickerRef.current) {
      onClose();
    }
  });

  useEffect(onCloseExpand, [isOpen]);

  const handleQuickEmojiPick = (emoji: string) => {
    onEmojiPick(emoji);
    onClose();
  };

  const handleEmojiSelect = (data: { native: string }) => {
    handleQuickEmojiPick(data.native);
  };

  if (isExpanded && layout === Layout.MOBILE) {
    return (
      <Portal>
        <Box
          position="absolute"
          width="100vw"
          height="100vh"
          top="0"
          bottom="0"
          left="0"
          right="0"
          bg={ModalStyleConfig.overlay.bg as string}
        >
          <Box
            ref={floatingPickerRef}
            position="absolute"
            width="100vw"
            height="90vh"
            top="10vh"
            left="0"
            right="0"
          >
            <style>
              {`
              em-emoji-picker  {
                width: 100%;
                height: 90vh;
              }
            `}
            </style>
            <Picker
              autoFocus
              theme={
                colorMode === 'system'
                  ? 'auto'
                  : colorMode === 'dark'
                  ? 'dark'
                  : 'light'
              }
              dynamicWidth={true}
              data={data}
              skinTonePosition="search"
              previewPosition="none"
              onEmojiSelect={handleEmojiSelect}
              i18n={buildPickerI18n()}
            />
          </Box>
        </Box>
      </Portal>
    );
  }

  return (
    <Popover placement="auto-start" isLazy isOpen={isOpen} onClose={onClose}>
      <PopoverTrigger>
        <Button
          aria-label={t`Pick emoji`}
          onClick={onOpen}
          variant="solid"
          size="xs"
          px={2}
          py={0}
          borderRadius="full"
          colorScheme="gray"
          sx={{
            '.chakra-popover__popper': {
              borderWidth: 0,
              borderColor: 'transparent',
            },
          }}
        >
          <AddReactionIcon />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        data-qa="emoji-picker"
        bg="transparent"
        boxShadow="none"
        border="none"
        // We are using explicit width here as using auto or no-value doesn't yield good placement on phone size screens
        width={isExpanded ? '354px' : 'auto'}
        height={isExpanded ? '435px' : 'auto'}
      >
        {isExpanded ? (
          <Picker
            autoFocus
            theme={
              colorMode === 'system'
                ? 'auto'
                : colorMode === 'dark'
                ? 'dark'
                : 'light'
            }
            data={data}
            skinTonePosition="search"
            previewPosition="none"
            onEmojiSelect={handleEmojiSelect}
            i18n={buildPickerI18n()}
          />
        ) : (
          <QuickPicker
            onExpandSelect={onExpand}
            onEmojiPick={handleQuickEmojiPick}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

type QuickPickerProps = {
  onExpandSelect: () => void;
  onEmojiPick: (emoji: string) => void;
};

const DefaultEmojiIds = ['+1', 'heart', 'joy', 'tada', 'raised_hands', '-1'];
const DefaultEmoji = DefaultEmojiIds.map((id) => {
  const defaultEmoji = (data as any).emojis[id];
  return {
    id: defaultEmoji.id as string,
    name: defaultEmoji.name as string,
    skins: defaultEmoji.skins.map(
      ({ native }: { native: string }) => native,
    ) as string[],
    neutral: defaultEmoji.skins[0].native as string,
  };
});

export const QuickPicker = ({
  onExpandSelect,
  onEmojiPick,
}: QuickPickerProps) => {
  const skin = (MartStore.get('skin') as EmojiSkin | undefined) || 1;
  const bgColor = useColorModeValue('gray.100', 'gray.700');
  const plusBgColor = useColorModeValue('gray.300', 'gray.900');

  return (
    <HStack
      p={2}
      borderRadius={120}
      bg={bgColor}
      borderColor={plusBgColor}
      borderWidth={1}
    >
      {DefaultEmoji.map(({ id, skins, neutral, name }) => (
        <IconButton
          key={id}
          onClick={() => onEmojiPick(skins[skin - 1] || neutral)}
          aria-label={name}
          fontSize={28}
          icon={<em-emoji id={id} skin={skin} />}
          borderRadius="full"
          size="sm"
          bg="transparent"
          _hover={{
            bg: 'transparent',
          }}
        />
      ))}
      <IconButton
        aria-label="Choose other Emoji"
        icon={<FontAwesomeIcon icon={solid('plus-large')} />}
        borderRadius="full"
        onClick={onExpandSelect}
        size="sm"
        bg={plusBgColor}
      />
    </HStack>
  );
};

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'em-emoji': EmEmojiAttributes;
    }

    interface EmEmojiAttributes {
      id?: string;
      native?: string;
      skin?: EmojiSkin;
    }
  }
}

// Let our translations do the translations
const buildPickerI18n = () => ({
  search: t({ id: 'emoji-picker.search', message: 'Search' }),
  search_no_results_1: t({
    id: 'emoji-picker.search_no_results_1',
    message: 'Oh no!',
  }),
  search_no_results_2: t({
    id: 'emoji-picker.search_no_results_2',
    message: 'That emoji couldn’t be found',
  }),
  pick: t({ id: 'emoji-picker.pick', message: 'Pick an emoji…' }),
  add_custom: t({
    id: 'emoji-picker.add_custom',
    message: 'Add custom emoji',
  }),
  categories: {
    activity: t(
      /*i18n*/ {
        id: 'emoji-picker.categories.activity',
        message: 'Activity',
      },
    ),
    custom: t({ id: 'emoji-picker.categories.custom', message: 'Custom' }),
    flags: t({ id: 'emoji-picker.categories.flags', message: 'Flags' }),
    foods: t({ id: 'emoji-picker.categories.foods', message: 'Food & Drink' }),
    frequent: t({
      id: 'emoji-picker.categories.frequent',
      message: 'Frequently used',
    }),
    nature: t({
      id: 'emoji-picker.categories.nature',
      message: 'Animals & Nature',
    }),
    objects: t({ id: 'emoji-picker.categories.objects', message: 'Objects' }),
    people: t({
      id: 'emoji-picker.categories.people',
      message: 'Smileys & People',
    }),
    places: t({
      id: 'emoji-picker.categories.places',
      message: 'Travel & Places',
    }),
    search: t({
      id: 'emoji-picker.categories.search',
      message: 'Search Results',
    }),
    symbols: t({ id: 'emoji-picker.categories.symbols', message: 'Symbols' }),
  },
  skins: {
    choose: t({
      id: 'emoji-picker.choose',
      message: 'Choose default skin tone',
    }),
    '1': t({ id: 'emoji-picker.skin-color.1', message: 'Default' }),
    '2': t({ id: 'emoji-picker.skin-color.2', message: 'Light' }),
    '3': t({ id: 'emoji-picker.skin-color.3', message: 'Medium-Light' }),
    '4': t({ id: 'emoji-picker.skin-color.4', message: 'Medium' }),
    '5': t({ id: 'emoji-picker.skin-color.5', message: 'Medium-Dark' }),
    '6': t({ id: 'emoji-picker.skin-color.6', message: 'Dark' }),
  },
});

// Instead of using the built-in react Picker from emoji-mart
//   we build our own.
// emoji-mart is created with a shadowRoot. This is great, until
//   you want to modify something in that root. We want to :)
// In order to not display the skin-tone names, we must inject
//   css into the shadow-root so we can hide the element with the name.
// We also inject some other styles.
const Picker = (props: any) => {
  const bgColorToken = useColorModeValue('gray.100', 'gray.700');
  const [bgColor] = useToken(
    // the key within the theme, in this case `theme.colors`
    'colors',
    [bgColorToken],
  );
  const ref = useRef<HTMLElement | undefined>(undefined);
  const instance = useRef<MartPicker | undefined>(undefined);

  if (instance.current) {
    instance.current.update(props);
  }

  useEffect(() => {
    instance.current = new MartPicker({ ...props, ref });

    return () => {
      instance.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (instance.current && ref.current) {
      const shadowRoot = ref.current.firstElementChild?.shadowRoot;
      if (shadowRoot) {
        let style = document.createElement('style');
        style.textContent = `
          .skin-tone ~ .margin-small-lr {
            padding: 0;
            clip: rect(0 0 0 0);
            clip-path: inset(50%);
            height: 1px;
            overflow: hidden;
            position: absolute;
            white-space: nowrap;
            width: 1px;
          }
          #root {
            background-color: ${bgColor} !important;
            --em-rgb-background: ${bgColor};
          }
        `;
        shadowRoot.appendChild(style);
      }
    }
  }, [instance, ref]);

  return React.createElement('div', { ref });
};
